/**
 * Agentic Workflow Service (Frontend)
 * Calls backend orchestrator that uses ADK agents with Gemini's native google_search
 * Uses async job queue pattern to avoid HTTP timeouts
 */

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

/**
 * Poll job status until completion
 * @param {string} jobId - Job ID to poll
 * @param {Function} onProgressUpdate - Progress callback
 * @param {Object} options - Polling options
 * @returns {Promise<Object>} - Job result
 */
async function pollJobUntilDone(jobId, onProgressUpdate = null, options = {}) {
  const { intervalMs = 2000, timeoutMs = 15 * 60 * 1000 } = options;
  const start = Date.now();

  while (true) {
    const elapsed = Date.now() - start;
    if (elapsed > timeoutMs) {
      throw new Error('Workflow polling timed out. Please try again later.');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/feasibility/agentic-workflow/status/${jobId}`);
      
      if (!response.ok) {
        throw new Error(`Status check failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.job) {
        throw new Error(data.message || 'Invalid job status response');
      }

      const job = data.job;

      // Update progress
      if (onProgressUpdate) {
        const progress = job.progress || 0;
        let message = 'Generating report...';
        
        if (progress < 25) {
          message = 'Analyzing protocol specifications...';
        } else if (progress < 50) {
          message = 'Gathering real-world insights...';
        } else if (progress < 75) {
          message = 'Building comprehensive analysis...';
        } else if (progress < 100) {
          message = 'Finalizing feasibility report...';
        } else {
          message = 'Report generation complete!';
        }
        
        onProgressUpdate({ progress, message });
      }

      // Check job status
      if (job.status === 'completed') {
        return job.result;
      }

      if (job.status === 'failed') {
        throw new Error(job.error || 'Workflow execution failed');
      }

      // Job still running, wait and poll again
      await new Promise(resolve => setTimeout(resolve, intervalMs));

    } catch (error) {
      // If it's not a timeout, throw immediately
      if (error.message && !error.message.includes('timed out')) {
        throw error;
      }
      // For timeout errors, wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
}

/**
 * Execute agentic workflow for country feasibility (async job-based)
 * @param {Object} input - Workflow input
 * @param {Function} onProgressUpdate - Progress callback
 * @returns {Promise<Object>} - Workflow results
 */
async function generateCountryFeasibility(input, onProgressUpdate = null) {
  try {
    // Initialize progress
    if (onProgressUpdate) {
      onProgressUpdate({ progress: 0, message: 'Starting workflow...' });
    }

    // Start job
    const startResponse = await fetch(`${API_BASE_URL}/feasibility/agentic-workflow/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input)
    });

    if (!startResponse.ok) {
      const errorData = await startResponse.json().catch(() => ({}));
      throw new Error(errorData.message || `Workflow start failed: ${startResponse.statusText}`);
    }

    const startData = await startResponse.json();
    
    if (!startData.success || !startData.jobId) {
      throw new Error(startData.message || 'Job start failed - no job ID returned');
    }

    const jobId = startData.jobId;
    console.log(`[Agentic Workflow] Job started: ${jobId}`);

    // Poll for completion
    const results = await pollJobUntilDone(
      jobId,
      onProgressUpdate,
      {
        intervalMs: 2000, // Poll every 2 seconds
        timeoutMs: 15 * 60 * 1000 // 15 minutes timeout
      }
    );

    return results;

  } catch (error) {
    console.error('[Agentic Workflow] Error:', error);
    throw error;
  }
}

/**
 * Get workflow status
 */
function getStatus() {
  return {
    available: true,
    version: '2.0.0',
    agents: ['SearchAgent', 'GenerationAgent', 'EvaluationAgent'],
    framework: 'Google ADK',
    searchTool: 'Gemini native google_search'
  };
}

const agenticWorkflowService = {
  generateCountryFeasibility,
  getStatus
};

export default agenticWorkflowService;
